/**
 * Door sheet state and the door setup per home (no React components here, so the
 * app bar chip can import it without pulling in the sheet).
 *
 *   Woodlands: switch.door_latch (Kivist latch strike) + Reolink doorbell
 *              camera.front_door_fluent (Frigate "frontdoor").
 *   Flat:      lock.door (ESPHome bolt/latch) + CAME intercom "open street door".
 */
import { useSyncExternalStore } from 'react';
import type { EntityState, HomeModel, ResolvedAccess, TalkMode } from '@home/shared';

export const LATCH = 'switch.door_latch';
export const LOCK = 'lock.door';
export const INTERCOM = 'button.front_door_came_front_door';
/** Doorbell camera, best first. */
const CAMERAS = ['camera.front_door_fluent', 'camera.frontdoor', 'camera.intercom'];
/** Frigate views of the doorbell (history). */
const HISTORY_CAMERAS = ['camera.front_door_fluent', 'camera.frontdoor', 'camera.front_door_clear'];
/** Doorbell presses: Reolink button / visitor, CAME ringing. */
export const RING_IDS = ['binary_sensor.door_bell_button', 'binary_sensor.front_door_visitor', 'binary_sensor.front_door_ringing'];
/** "Rang recently" window for the chip. */
export const RING_WINDOW_S = 120;

export interface DoorSetup {
  latch: string | null;
  lock: string | null;
  intercom: string | null;
  camera: string | null;
  historyCameras: string[];
  rings: string[];
}

export function doorSetup(model: HomeModel | undefined): DoorSetup {
  const has = (id: string): boolean => !!model?.entities[id];
  return {
    latch: has(LATCH) ? LATCH : null,
    lock: has(LOCK) ? LOCK : null,
    intercom: has(INTERCOM) ? INTERCOM : null,
    camera: CAMERAS.find(has) ?? null,
    historyCameras: HISTORY_CAMERAS.filter(has),
    rings: RING_IDS.filter(has),
  };
}

export const hasDoor = (d: DoorSetup): boolean => !!(d.latch || d.lock || d.intercom || (d.camera && d.camera !== 'camera.intercom') || d.rings.length);

/** The chip shows for the door module, the door_unlock / intercom_open privilege, or admins, in homes with a door. */
export function canSeeDoor(model: HomeModel | undefined, access: ResolvedAccess | undefined): boolean {
  if (!model || !access) return false;
  const allowed =
    access.isAdmin ||
    (access.modules.includes('door') && model.modules.includes('door')) ||
    access.privileges.includes('door_unlock') ||
    access.privileges.includes('intercom_open');
  return allowed && hasDoor(doorSetup(model));
}

export type DoorTone = 'ring' | 'open' | 'secure' | 'unknown';

/** Unlocked / released → open; locked / latched → secure. */
export function lockTone(id: string | null, s: EntityState | undefined): DoorTone {
  if (!id || !s || s.s === 'unavailable' || s.s === 'unknown') return 'unknown';
  if (id.startsWith('lock.')) return s.s === 'locked' || s.s === 'locking' ? 'secure' : 'open';
  if (id.startsWith('switch.')) return s.s === 'on' ? 'open' : 'secure';
  return 'unknown';
}

export function lockLabel(id: string, s: EntityState | undefined): string {
  const v = s?.s;
  if (!v || v === 'unavailable') return 'Unavailable';
  if (id.startsWith('lock.')) {
    return ({ locked: 'Locked', unlocked: 'Unlocked', locking: 'Locking…', unlocking: 'Unlocking…', open: 'Open', opening: 'Opening…', jammed: 'Jammed' } as Record<string, string>)[v] ?? 'Unknown';
  }
  if (v === 'on') return 'Released';
  if (v === 'off') return 'Latched';
  return 'Ready';
}

// ---------------------------------------------------------------- rings

const seen = new Map<string, number>();
/** Remember a ring we saw happen (binary sensors flip back off within seconds). */
export function noteRing(home: string, at: number): void {
  if (at > (seen.get(home) ?? 0)) seen.set(home, at);
}
/** Epoch seconds of the latest ring we know of for this home (an "on" ring sensor counts as now-ish). */
export function lastRing(home: string, states: Array<EntityState | undefined>): number | null {
  let t = seen.get(home) ?? 0;
  for (const s of states) if (s?.s === 'on' && s.lc > t) t = s.lc;
  return t || null;
}
export const ringingNow = (last: number | null, nowS = Date.now() / 1000): boolean => last !== null && nowS - last < RING_WINDOW_S;

// ---------------------------------------------------------------- sheet signal

export interface DoorTarget {
  home: string;
  /** Start talk / listen as the sheet opens; `n` makes repeated requests distinct. */
  talk: { mode: TalkMode; n: number } | null;
}

let target: DoorTarget | null = null;
let seq = 0;
const ls = new Set<() => void>();
const set = (t: DoorTarget | null): void => {
  target = t;
  ls.forEach((fn) => fn());
};

export const openDoor = (home: string, talk?: TalkMode): void => set({ home, talk: talk ? { mode: talk, n: ++seq } : null });
export const closeDoor = (): void => set(null);
export const doorTarget = (): DoorTarget | null => target;
export function useDoorTarget(): DoorTarget | null {
  return useSyncExternalStore(
    (fn) => {
      ls.add(fn);
      return () => ls.delete(fn);
    },
    () => target,
    () => target,
  );
}
